import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Testimonials() {
  const { t } = useTranslation();
  const [liveReviews, setLiveReviews] = useState([]);

  const staticReviews = [
    { key: 't1', rating: 5 },
    { key: 't2', rating: 5 },
    { key: 't3', rating: 5 },
    { key: 't4', rating: 5 }
  ];

  useEffect(() => {
    axios.get(`${API}/reviews`).then(res => {
      setLiveReviews(res.data.reviews || []);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center font-outfit mb-10">
          {t('about.testimonialsTitle')}
        </h2>

        {/* Live customer reviews */}
        {liveReviews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {liveReviews.slice(0, 6).map((review, i) => (
              <motion.div
                key={review.created_at + i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-teal-500/5 to-white/5 border border-teal-500/20 rounded-2xl p-5 flex flex-col"
                data-testid={`live-review-${i}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-0.5">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                    {Array.from({ length: 5 - review.rating }).map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5 text-slate-600" />
                    ))}
                  </div>
                  <span className="text-xs text-teal-400/60 bg-teal-500/10 px-2 py-0.5 rounded-full">Verified</span>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed flex-1 mb-4">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-medium">{review.name}</p>
                    <p className="text-slate-500 text-xs">{review.destination}</p>
                  </div>
                  <p className="text-slate-600 text-xs">
                    {new Date(review.created_at).toLocaleDateString('it-IT', { month: 'short', year: 'numeric' })}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Static reviews */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {staticReviews.map((review, i) => (
            <motion.div
              key={review.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col"
              data-testid={`home-testimonial-${i}`}
            >
              <Quote className="w-5 h-5 text-teal-500/40 mb-3" />
              <p className="text-slate-300 text-sm leading-relaxed flex-1 mb-4">
                {t(`about.testimonials.${review.key}.text`)}
              </p>
              <div className="flex gap-0.5 mb-2">
                {Array.from({ length: review.rating }).map((_, j) => (
                  <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <div>
                <p className="text-white text-sm font-medium">{t(`about.testimonials.${review.key}.name`)}</p>
                <p className="text-slate-500 text-xs">{t(`about.testimonials.${review.key}.location`)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
